import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FeedbackNotification {
  id: string;
  title: string;
  feedback_type: 'suggestion' | 'bug' | 'other' | 'question';
  status: 'pending' | 'resolved' | 'in_progress' | 'rejected';
  created_at: string;
  user_id: string | null;
  anonymous_name: string | null;
}

export const useFeedbackNotifications = (userRole: string | null) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [latestFeedback, setLatestFeedback] = useState<FeedbackNotification | null>(null);

  // Only fetch if user is admin or moderator
  const { data: feedbackData, refetch } = useQuery({
    queryKey: ['feedback-notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback')
        .select('id, title, feedback_type, status, created_at, user_id, anonymous_name')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: userRole === 'admin' || userRole === 'moderator',
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Set up real-time subscription
  useEffect(() => {
    if (userRole !== 'admin' && userRole !== 'moderator') return;

    const channel = supabase
      .channel('feedback-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'feedback'
        },
        (payload) => {
          console.log('New feedback received:', payload);
          const newFeedback = payload.new as FeedbackNotification;
          
          // Show toast notification
          toast.success('New Feedback Received', {
            description: `New ${newFeedback.feedback_type}: ${newFeedback.title}`,
            action: {
              label: 'View',
              onClick: () => window.location.href = '/feedback-management'
            }
          });

          // Update latest feedback
          setLatestFeedback(newFeedback);
          
          // Refetch data to update count
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'feedback'
        },
        () => {
          // Refetch when feedback status changes
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userRole, refetch]);

  // Update pending count when data changes
  useEffect(() => {
    if (feedbackData) {
      setPendingCount(feedbackData.length);
    }
  }, [feedbackData]);

  return {
    pendingCount,
    latestFeedback,
    feedbackData: feedbackData || [],
    refetch
  };
};